/**
 * Draw a parsed carer-guidance text (single-text.ts) as the carer sees it —
 * the Option B look Ed approved 2026-09-29. Used by the catalogue editor's
 * live preview and the plan editor's override preview, so an author sees the
 * carer's screen before saving. Styles: `.gtx-*` in index.html.template. The
 * carer app draws the same blocks in Flutter (app.carer guidance sheet).
 */
import { escapeHtml } from "../utils/html";
import type { GuidanceBlock, GuidanceDoc, GuidanceFold } from "./single-text";

/** Escape, then the one inline rule: **bold**. */
function inline(s: string): string {
  return escapeHtml(s).replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
}

const ICON = {
  check: `<svg class="gtx-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9"/><path d="M8 11l3 3 9-9"/></svg>`,
  pen: `<svg class="gtx-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 3l5 5L9 20H4v-5z"/><path d="M13 21h8"/></svg>`,
  info: `<svg class="gtx-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/></svg>`,
  lock: `<svg class="gtx-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>`,
};

function iconFor(title: string): string {
  if (/before/i.test(title)) return ICON.check;
  if (/record|score/i.test(title)) return ICON.pen;
  return ICON.info;
}

function block(b: GuidanceBlock): string {
  switch (b.kind) {
    case "label":
      return `<div class="gtx-label">${inline(b.text)}</div>`;
    case "para":
      return `<p class="gtx-p">${inline(b.text)}</p>`;
    case "steps":
      return `<ol class="gtx-list">${b.items.map((t) => `<li>${inline(t)}</li>`).join("")}</ol>`;
    case "bullets":
      return `<ul class="gtx-list">${b.items.map((t) => `<li>${inline(t)}</li>`).join("")}</ul>`;
    case "scale":
      return `<ul class="gtx-scale">${b.items
        .map((it) => `<li><span class="gtx-num gtx-num-sm">${it.n}</span><span><b>${inline(it.name)}</b>${it.text ? `<br>${inline(it.text)}` : ""}</span></li>`)
        .join("")}</ul>`;
    case "note":
      return `<div class="gtx-note">${ICON.lock}<div>${b.paras.map((p) => `<p class="gtx-p">${inline(p)}</p>`).join("")}</div></div>`;
  }
}

function fold(f: GuidanceFold, open: boolean): string {
  const head = f.level !== null ? `<span class="gtx-num">${f.level}</span>` : iconFor(f.title);
  const body = f.blocks.length ? f.blocks.map(block).join("") : `<p class="gtx-p gtx-empty">(empty)</p>`;
  return `<details class="gtx-fold ${f.level !== null ? "gtx-level" : "gtx-info"}"${open ? " open" : ""}>` +
    `<summary>${head}<span class="gtx-title">${inline(f.title)}</span><span class="gtx-chev" aria-hidden="true"></span></summary>` +
    `<div class="gtx-body">${body}</div></details>`;
}

/**
 * The carer's guidance screen for one text. `open` says which folds start
 * open (by index); default = the first one only, as in the carer app.
 */
export function renderGuidanceHtml(doc: GuidanceDoc, open: (i: number) => boolean = (i) => i === 0): string {
  const parts: string[] = [];
  if (doc.intro.length) parts.push(`<div class="gtx-intro">${doc.intro.map(block).join("")}</div>`);
  let levelLabelDone = false;
  doc.folds.forEach((f, i) => {
    if (f.level !== null && !levelLabelDone) {
      parts.push(`<div class="gtx-group">Open the level that matches what you're seeing</div>`);
      levelLabelDone = true;
    }
    parts.push(fold(f, open(i)));
  });
  if (!parts.length) parts.push(`<p class="gtx-p gtx-empty">No carer guidance yet.</p>`);
  return `<div class="gtx">${parts.join("")}</div>`;
}
